import { useCallback, useEffect, useState } from "react";
import { ChartSnapshot } from "@/components/ChartSnapshot";
import { api, errMsg, num, rupee, pnlClass, istTime, STRATEGY_LABELS } from "@/lib/api";

const KIND = STRATEGY_LABELS;

const Box = ({ title, children, id }) => (
  <div className="rounded border border-slate-800 bg-slate-900/40 p-3" data-testid={id}>
    <div className="eyebrow !text-[10px]">{title}</div>
    <div className="num mt-2 space-y-1 text-xs">{children}</div>
  </div>
);
const Row = ({ k, v, cls = "text-slate-100" }) => <div className="flex justify-between gap-3"><span className="text-slate-500">{k}</span><span className={cls}>{v}</span></div>;
const Res = ({ x }) => !x ? <span className="text-slate-600">—</span> : (
  <span className={pnlClass(x.pnl)}>{x.direction?.[0]} {x.pnl !== undefined && x.pnl !== null ? rupee(x.pnl) : x.status}{x.reason || x.exit_reason ? ` · ${x.reason || x.exit_reason}` : ""}</span>
);

export const DailyReport = () => {
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback((date) => {
    setErr(null);
    api.get("/reports/daily", { params: date ? { date } : {} }).then((r) => setRep(r.data)).catch((e) => setErr(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (err) return <div className="panel p-4 text-xs text-red-400" data-testid="daily-report-error">{err}</div>;
  if (!rep) return null;
  const { dry, paper, backtest: bt, expected_per_day: ex } = rep;
  const dates = rep.available_dates.includes(rep.date) ? rep.available_dates : [rep.date, ...rep.available_dates];
  return (
    <div className="panel p-4" data-testid="panel-daily-report">
      <div className="flex flex-wrap items-center gap-3">
        <span className="eyebrow">Daily rehearsal report · {KIND[rep.strategy_type]}</span>
        <select data-testid="select-report-date" value={rep.date} onChange={(e) => load(e.target.value)} className="num ml-auto h-8 rounded-md border border-input bg-slate-900 px-2 text-xs">
          {dates.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
      <ChartSnapshot date={rep.date} />
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
        <Box title="Dry run (simulated real orders)" id="report-dry">
          <Row k="Trades closed" v={dry.trades} /><Row k="Unfilled / aborted" v={dry.aborted} />
          <Row k="P&L" v={rupee(dry.pnl)} cls={pnlClass(dry.pnl)} /><Row k="Win rate" v={dry.win_rate === null ? "—" : `${num(dry.win_rate)}%`} />
        </Box>
        <Box title="Fills & slippage (dry run)" id="report-slippage">
          <Row k="Avg entry slip" v={dry.avg_entry_slip_pts === null ? "—" : `${num(dry.avg_entry_slip_pts)} pts`} cls={dry.avg_entry_slip_pts > 0 ? "down" : "text-slate-100"} />
          <Row k="Avg exit slip" v={dry.avg_exit_slip_pts === null ? "—" : `${num(dry.avg_exit_slip_pts)} pts`} cls={dry.avg_exit_slip_pts > 0 ? "down" : "text-slate-100"} />
          <Row k="Slippage cost" v={rupee(-dry.slip_rupees)} cls={dry.slip_rupees > 0 ? "down" : "text-slate-100"} />
          <div className="text-[10px] text-slate-600">vs quoted ask/bid when the signal fired</div>
        </Box>
        <Box title="Paper ledger" id="report-paper">
          <Row k="Trades closed" v={paper.trades} /><Row k="Open" v={paper.open} />
          <Row k="P&L" v={rupee(paper.pnl)} cls={pnlClass(paper.pnl)} /><Row k="Win rate" v={paper.win_rate === null ? "—" : `${num(paper.win_rate)}%`} />
        </Box>
        <Box title="Backtest expected" id="report-backtest">
          <Row k="Same-day backtest" v={`${bt.trades} · ${rupee(bt.pnl)}`} cls={pnlClass(bt.pnl)} />
          {ex && <Row k="3y avg per day" v={`${num(ex.trades)} · ${rupee(ex.pnl)}`} />}
          {ex && <Row k="3y expectancy/trade" v={rupee(ex.expectancy)} />}
          {bt.error && <div className="text-[10px] text-amber-400">{bt.error}</div>}
        </Box>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="dense num w-full" data-testid="table-report-signals">
          <thead><tr><th>Signal candle</th><th>Backtest</th><th>Dry run</th><th>Paper</th></tr></thead>
          <tbody>
            {rep.signals.length === 0 && <tr><td colSpan={4} className="!py-5 !text-center text-slate-500">No signals on {rep.date}.</td></tr>}
            {rep.signals.map((x) => (
              <tr key={x.signal_ts}><td>{istTime(x.signal_ts)}</td><td><Res x={x.backtest} /></td><td><Res x={x.dry} /></td><td><Res x={x.paper} /></td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[11px] text-slate-500">A signal in the backtest column with "—" in dry run or paper means the live engine missed it, for example because of a token, proxy or app-offline gap.</p>
    </div>
  );
};
